import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  TextField,
  Button,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Assessment as ReportIcon,
  TrendingUp as RevenueIcon,
  AccountBalance as BalanceIcon,
  CreditCard as CreditCardIcon,
  People as PeopleIcon,
  AutoAwesome as PremiumIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from '@mui/material/styles';
import apiService from '../../services/api';
import { SubscriptionPlanBreakdown, Payment, PaymentTransactionsResponse } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const FinancialReportingPage: React.FC = () => {
  const { hasAnyPermission } = useAuth();
  const canViewSubscriptions = hasAnyPermission(['admin.subscriptions.view', 'admin.subscriptions.manage']);
  const theme = useTheme();
  const [report, setReport] = useState<any>(null);
  const [subscriptionStats, setSubscriptionStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('monthly');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [transactionFilters, setTransactionFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
  });
  const [transactions, setTransactions] = useState<Payment[]>([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsPagination, setTransactionsPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exporting, setExporting] = useState(false);
  const TRANSACTIONS_PER_PAGE = 10;

  const fetchFinancialReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      if (!canViewSubscriptions) {
        setSubscriptionStats({
          total_revenue: 0,
          active_subscriptions: 0,
          total_subscriptions: 0,
          revenue_this_month: 0,
          subscription_growth_percentage: 0,
          connect_plan_active: 0,
          plan_breakdown: [],
          expired_subscriptions: 0,
          cancelled_subscriptions: 0,
        });
        setReport({
          period,
          total_revenue: 0,
          revenue_this_month: 0,
          active_subscriptions: 0,
          total_subscriptions: 0,
          average_revenue_per_subscription: 0,
          subscription_growth: 0,
          connect_plan_active: 0,
        });
        return;
      }

      const subscriptionData = await apiService.getSubscriptionStatistics({ period });
      setSubscriptionStats(subscriptionData);

      // Calculate financial report based on subscriptions only
      const totalSubscriptionRevenue = subscriptionData.total_revenue || 0;
      const activeSubscriptions = subscriptionData.active_subscriptions || 0;
      const totalSubscriptions = subscriptionData.total_subscriptions || 0;
      const revenueThisMonth = subscriptionData.revenue_this_month || 0;

      // Calculate metrics
      const avgRevenuePerSubscription = activeSubscriptions > 0 ? totalSubscriptionRevenue / activeSubscriptions : 0;
      const subscriptionGrowth = subscriptionData.subscription_growth_percentage || 0;
      const connectPlanActive = subscriptionData.connect_plan_active || 0;

      const calculatedReport = {
        period: period,
        total_revenue: totalSubscriptionRevenue,
        revenue_this_month: revenueThisMonth,
        active_subscriptions: activeSubscriptions,
        total_subscriptions: totalSubscriptions,
        average_revenue_per_subscription: avgRevenuePerSubscription,
        subscription_growth: subscriptionGrowth,
        connect_plan_active: connectPlanActive,
      };

      setReport(calculatedReport);
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.message || 'Failed to load financial report');
    } finally {
      setIsLoading(false);
    }
  }, [period, canViewSubscriptions]);

  useEffect(() => {
    fetchFinancialReport();
  }, [fetchFinancialReport]);

  const fetchTransactions = useCallback(async () => {
    try {
      setTransactionsLoading(true);
      const response = await apiService.getPaymentTransactions({
        page: transactionsPage,
        per_page: TRANSACTIONS_PER_PAGE,
        date_from: transactionFilters.dateFrom || undefined,
        date_to: transactionFilters.dateTo || undefined,
        status: transactionFilters.status ? (transactionFilters.status as 'success' | 'failure' | 'cancelled') : undefined,
      });
      setTransactions(response.transactions);
      setTransactionsPagination(response.pagination);
    } catch (err: any) {
      console.error('Failed to load transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  }, [transactionsPage, transactionFilters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    setTransactionsPage(1);
  }, [transactionFilters]);

  const handleExportTransactions = useCallback(async () => {
    try {
      setExportError('');
      setExporting(true);
      const params = {
        date_from: transactionFilters.dateFrom || undefined,
        date_to: transactionFilters.dateTo || undefined,
        status: transactionFilters.status
          ? (transactionFilters.status as 'success' | 'failure' | 'cancelled')
          : undefined,
      };

      const csvBlob = await apiService.exportPaymentTransactions(params);
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment-transactions-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export transactions:', err);
      setExportError(err?.response?.data?.message || 'Failed to download transactions');
    } finally {
      setExporting(false);
    }
  }, [transactionFilters]);

  const planBreakdown: SubscriptionPlanBreakdown[] = subscriptionStats?.plan_breakdown || [];
  const filteredPlans = useMemo(
    () =>
      selectedPlanId
        ? planBreakdown.filter((plan) => String(plan.plan_id) === selectedPlanId)
        : planBreakdown,
    [planBreakdown, selectedPlanId]
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!report || !subscriptionStats) {
    return null;
  }

  // Calculate subscription distribution
  const subscriptionDistribution = [
    { name: 'Active', value: subscriptionStats.active_subscriptions || 0, color: theme.palette.success.main },
    { name: 'Inactive', value: (subscriptionStats.total_subscriptions || 0) - (subscriptionStats.active_subscriptions || 0), color: theme.palette.error.main },
  ].filter(item => item.value > 0);

  const totalActive = report.active_subscriptions || 0;

  return (
    <Box>
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Subscription Revenue Report
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Monitor your subscription-based income and customer metrics
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value as any)}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics Cards */}
      {canViewSubscriptions && (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Revenue */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${theme.palette.success.main}20, ${theme.palette.success.light}20)`, border: `1px solid ${theme.palette.success.light}` }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                    Total Revenue
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: theme.palette.success.main }}>
                    ₹{report.total_revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    All-time earnings
                  </Typography>
                </Box>
                <RevenueIcon sx={{ fontSize: 40, color: theme.palette.success.main, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* This Month Revenue */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.light}20)`, border: `1px solid ${theme.palette.primary.light}` }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                    This Month Revenue
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: theme.palette.primary.main }}>
                    ₹{report.revenue_this_month.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Typography>
                  <Chip 
                    label={`${report.subscription_growth.toFixed(1)}% growth`} 
                    size="small" 
                    sx={{ mt: 0.5, backgroundColor: theme.palette.success.light, color: theme.palette.success.dark }}
                  />
                </Box>
                <CreditCardIcon sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Subscriptions */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${theme.palette.info.main}20, ${theme.palette.info.light}20)`, border: `1px solid ${theme.palette.info.light}` }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                    Active Subscriptions
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: theme.palette.info.main }}>
                    {report.active_subscriptions}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    of {report.total_subscriptions} total
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: theme.palette.info.main, opacity: 0.3 }} />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(report.active_subscriptions / (report.total_subscriptions || 1)) * 100} 
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Avg Revenue per Subscription */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${theme.palette.warning.main}20, ${theme.palette.warning.light}20)`, border: `1px solid ${theme.palette.warning.light}` }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                    Avg Per Subscription
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: theme.palette.warning.main }}>
                    ₹{report.average_revenue_per_subscription.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    Average revenue
                  </Typography>
                </Box>
                <PremiumIcon sx={{ fontSize: 40, color: theme.palette.warning.main, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Charts Section */}
      {canViewSubscriptions && (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Subscription Status Distribution */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Subscription Status
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {subscriptionDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={subscriptionDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }: any) => `${name}: ${value} (${((percent || 0) * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {subscriptionDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="textSecondary">No subscription data available</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Plans */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Subscription Plans Breakdown
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planBreakdown.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Plan</InputLabel>
                    <Select
                      value={selectedPlanId}
                      label="Plan"
                      onChange={(event) => setSelectedPlanId(event.target.value)}
                    >
                      <MenuItem value="">All Plans</MenuItem>
                      {planBreakdown.map((plan) => (
                        <MenuItem key={plan.plan_id} value={String(plan.plan_id)}>
                          {plan.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {/* Plan cards */}
                {filteredPlans.length > 0 ? filteredPlans.map((plan: SubscriptionPlanBreakdown) => {
                  const shareOfActive = totalActive > 0 ? (plan.active_subscriptions / totalActive) * 100 : 0;
                  const planActiveRatio = plan.total_subscriptions > 0 ? (plan.active_subscriptions / plan.total_subscriptions) * 100 : 0;
                  const planRevenueLabel = plan.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 });
                  const monthlyRevenueLabel = plan.monthly_revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 });

                  return (
                    <Paper key={plan.plan_id} sx={{ p: 2, backgroundColor: theme.palette.background.default, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box>
                          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                            {plan.name}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                            ₹{Number(plan.price || 0).toFixed(0)} · {plan.duration_days} days
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: 60, 
                          height: 60, 
                          borderRadius: '50%', 
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: 24
                        }}>
                          {plan.active_subscriptions}
                        </Box>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(shareOfActive, 100)} 
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          {plan.total_subscriptions > 0 ? planActiveRatio.toFixed(1) : 0}% active within the plan
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {shareOfActive.toFixed(1)}% share of active base
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip 
                          label={`₹${planRevenueLabel} total revenue`} 
                          size="small" 
                          variant="outlined" 
                          color="primary" 
                        />
                        <Chip 
                          label={`₹${monthlyRevenueLabel} this month`} 
                          size="small" 
                          variant="outlined" 
                          color="secondary" 
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Inactive: {plan.inactive_subscriptions}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Expired: {plan.expired_subscriptions}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Cancelled: {plan.cancelled_subscriptions}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                }) : (
                  <Typography color="textSecondary">No plan-level data available.</Typography>
                )}

                {/* Subscription Stats Summary */}
                <Paper sx={{ p: 2, backgroundColor: theme.palette.background.default }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Overview
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Active</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                        {report.active_subscriptions}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Expired</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                        {subscriptionStats.expired_subscriptions || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Cancelled</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                        {subscriptionStats.cancelled_subscriptions || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">Total</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {report.total_subscriptions}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Subscription Details Table */}
      {canViewSubscriptions && (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Subscription Summary
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.background.default }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Metric</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Value</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Total Subscriptions</TableCell>
                      <TableCell align="right">{report.total_subscriptions}</TableCell>
                      <TableCell align="right">100%</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: theme.palette.success.light + '20' }}>
                      <TableCell>Active Subscriptions</TableCell>
                      <TableCell align="right"><Chip label={report.active_subscriptions} size="small" color="success" variant="outlined" /></TableCell>
                      <TableCell align="right">{report.total_subscriptions > 0 ? ((report.active_subscriptions / report.total_subscriptions) * 100).toFixed(1) : 0}%</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: theme.palette.error.light + '20' }}>
                      <TableCell>Inactive Subscriptions</TableCell>
                      <TableCell align="right"><Chip label={report.total_subscriptions - report.active_subscriptions} size="small" color="error" variant="outlined" /></TableCell>
                      <TableCell align="right">{report.total_subscriptions > 0 ? (((report.total_subscriptions - report.active_subscriptions) / report.total_subscriptions) * 100).toFixed(1) : 0}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Expired Subscriptions</TableCell>
                      <TableCell align="right">{subscriptionStats.expired_subscriptions || 0}</TableCell>
                      <TableCell align="right">{report.total_subscriptions > 0 ? (((subscriptionStats.expired_subscriptions || 0) / report.total_subscriptions) * 100).toFixed(1) : 0}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Cancelled Subscriptions</TableCell>
                      <TableCell align="right">{subscriptionStats.cancelled_subscriptions || 0}</TableCell>
                      <TableCell align="right">{report.total_subscriptions > 0 ? (((subscriptionStats.cancelled_subscriptions || 0) / report.total_subscriptions) * 100).toFixed(1) : 0}%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Transaction Grid */}
      <Box mb={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Filter Transactions
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
                <TextField
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={transactionFilters.dateFrom}
                  onChange={(event) =>
                    setTransactionFilters((prev) => ({ ...prev, dateFrom: event.target.value }))
                  }
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={transactionFilters.dateTo}
                  onChange={(event) =>
                    setTransactionFilters((prev) => ({ ...prev, dateTo: event.target.value }))
                  }
                  sx={{ minWidth: 150 }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={transactionFilters.status}
                    onChange={(event) =>
                      setTransactionFilters((prev) => ({ ...prev, status: event.target.value }))
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="failure">Failure</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setTransactionsPage(1);
                    fetchTransactions();
                  }}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleExportTransactions}
                  disabled={exporting}
                >
                  {exporting ? 'Preparing Excel…' : 'Download Excel'}
                </Button>
              </Stack>
              {exportError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {exportError}
                </Alert>
              )}
            </Paper>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.background.default }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Patient</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 && !transactionsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{payment.payment_reference}</TableCell>
                        <TableCell>{payment.patient?.name || '—'}</TableCell>
                        <TableCell>{`₹${payment.amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}</TableCell>
                        <TableCell>{payment.status}</TableCell>
                        <TableCell>{payment.created_at}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {transactionsLoading && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={transactionsPagination.last_page}
                page={transactionsPage}
                onChange={(_e, value) => setTransactionsPage(value)}
                disabled={transactionsLoading}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default FinancialReportingPage;

